import React, { useState, useEffect } from 'react';
import { SiteHeader, SiteFooter } from '@/components/SiteLayout';

const SECTIONS = [
  { id: 'acceptance', title: '1. Acceptance of Terms' },
  { id: 'definitions', title: '2. Definitions' },
  { id: 'description', title: '3. Description of Service' },
  { id: 'eligibility', title: '4. Eligibility & Registration' },
  { id: 'billing', title: '5. Subscription Plans & Billing' },
  { id: 'refunds', title: '6. Refund Policy' },
  { id: 'acceptable-use', title: '7. Acceptable Use' },
  { id: 'crypto-risks', title: '8. Cryptocurrency Risks' },
  { id: 'chargebacks', title: '9. Chargebacks & Disputes' },
  { id: 'payment-verification', title: '10. Payment Verification' },
  { id: 'sla', title: '11. Service Level Agreement' },
  { id: 'intellectual-property', title: '12. Intellectual Property' },
  { id: 'confidentiality', title: '13. Confidentiality & API Keys' },
  { id: 'third-party', title: '14. Third-Party Services' },
  { id: 'indemnification', title: '15. Indemnification' },
  { id: 'liability', title: '16. Limitation of Liability' },
  { id: 'warranties', title: '17. Disclaimer of Warranties' },
  { id: 'force-majeure', title: '18. Force Majeure' },
  { id: 'termination', title: '19. Termination' },
  { id: 'governing-law', title: '20. Governing Law' },
  { id: 'changes', title: '21. Changes to Terms' },
  { id: 'contact', title: '22. Contact' },
];

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="text-base font-bold text-foreground mb-4 pb-2 border-b border-border">{title}</h2>
    {children}
  </section>
);

const Para = ({ children }) => (
  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{children}</p>
);

const List = ({ items }) => (
  <ul className="space-y-1.5 text-sm text-muted-foreground mb-3 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary/60 shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const SubSection = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="text-sm font-semibold text-foreground mb-2">{title}</h3>
    {children}
  </div>
);

const InfoBox = ({ children, variant = 'default' }) => {
  const styles = {
    default:   'bg-muted/30 border-border',
    warning:   'bg-amber-500/5 border-amber-500/20',
    important: 'bg-primary/5 border-primary/20',
    info:      'bg-blue-500/5 border-blue-500/20 text-blue-200',
  };
  return (
    <div className={`rounded-lg border p-4 my-4 text-sm text-muted-foreground ${styles[variant]}`}>
      {children}
    </div>
  );
};

export default function TermsPage() {
  const [activeSection, setActiveSection] = useState('acceptance');

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <main className="flex-1">
      <div className="max-w-7xl mx-auto px-6 py-16" data-testid="terms-page">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary mb-3 block">Legal</span>
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 tracking-tight">Terms &amp; Conditions</h1>
          <p className="text-sm text-muted-foreground">Last updated: <strong className="text-foreground">February 2026</strong> &nbsp;·&nbsp; Effective: <strong className="text-foreground">February 2026</strong></p>
          <p className="text-sm text-muted-foreground mt-2">Please read these Terms carefully before using CoinPayUp. They govern your use of our platform and constitute a legally binding agreement between you and CoinPayUp.</p>
        </div>

        <div className="flex gap-10 items-start">
          {/* Sticky Table of Contents */}
          <aside className="hidden xl:block w-56 shrink-0 sticky top-24">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Contents</p>
            <nav className="space-y-1 max-h-[75vh] overflow-y-auto pr-1">
              {SECTIONS.map(({ id, title }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={(e) => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}
                  className={`block text-xs py-1.5 px-2.5 rounded-md transition-all ${
                    activeSection === id
                      ? 'text-primary bg-primary/8 font-semibold'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
                  }`}
                >
                  {title}
                </a>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 max-w-3xl bg-card border border-border rounded-2xl p-8 lg:p-10">

            <Section id="acceptance" title="1. Acceptance of Terms">
              <Para>
                By accessing, registering for, or using CoinPayUp's website and services (the <strong className="text-foreground">"Service"</strong>), you (<strong className="text-foreground">"Merchant"</strong>, <strong className="text-foreground">"User"</strong>, or <strong className="text-foreground">"you"</strong>) agree to be legally bound by these Terms and Conditions (<strong className="text-foreground">"Terms"</strong>). These Terms apply to all users, including merchants, visitors, API integrators, and others who access the platform.
              </Para>
              <Para>
                If you do not agree with any part of these Terms, you must immediately cease using the Service. If you are using the Service on behalf of a business, you represent that you have the authority to bind that business to these Terms.
              </Para>
              <Para>
                Use of the Service is also governed by our <a href="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</a>, which is incorporated into these Terms by reference. By using the Service, you also agree to the collection and use of your information as described in the Privacy Policy.
              </Para>
              <InfoBox variant="important">
                These Terms were last updated in February 2026. Continued use of the Service after updates are posted constitutes acceptance of the revised Terms.
              </InfoBox>
            </Section>

            <Section id="definitions" title="2. Definitions">
              <List items={[
                '"CoinPayUp", "we", "us", or "our" — refers to the CoinPayUp platform and its operators',
                '"Service" — the CoinPayUp cryptocurrency payment gateway, dashboard, APIs, plugins, and all related products',
                '"Merchant" or "User" — any individual or business that registers for and uses the Service',
                '"Invoice" — a payment request created by a Merchant through the Service, specifying an amount and receiving wallet address',
                '"Transaction" — an on-chain transfer of cryptocurrency recorded on a public blockchain',
                '"Wallet" — a blockchain address controlled by the Merchant where payments are received',
                '"API Key" — a cryptographic credential that authorises programmatic access to the Service',
                '"Plan" — a subscription tier (Free, Starter, Business, Enterprise) that determines feature access and fee structure',
                '"Fee" — a percentage or fixed charge applied by CoinPayUp to processed transaction volumes',
              ]} />
            </Section>

            <Section id="description" title="3. Description of Service">
              <Para>CoinPayUp is a <strong className="text-foreground">non-custodial cryptocurrency payment verification platform</strong> that enables Merchants to:</Para>
              <List items={[
                'Create and manage cryptocurrency payment invoices for customers',
                'Receive real-time on-chain payment verification across BSC, Ethereum, Polygon, and Tron networks',
                'Integrate payment acceptance into websites and applications via REST API, payment links, and e-commerce plugins',
                'Monitor blockchain node health, payment status, and transaction history',
                'Configure payment webhooks for automated order fulfillment',
                'Access detailed billing history, fee ledgers, and financial reports',
                'Manage team access with role-based permissions (where available on your plan)',
              ]} />
              <InfoBox variant="warning">
                <strong className="text-foreground">CoinPayUp is not a custodial service, exchange, or financial institution.</strong> We do not hold, manage, or have access to cryptocurrency funds at any time. All payments flow directly to Merchant-controlled blockchain wallets.
              </InfoBox>
            </Section>

            <Section id="eligibility" title="4. Eligibility & Registration">
              <SubSection title="4.1 Eligibility Requirements">
                <Para>To register and use CoinPayUp, you must:</Para>
                <List items={[
                  'Be at least 18 years of age, or the age of legal majority in your jurisdiction',
                  'Have the legal capacity and authority to enter into binding contracts',
                  'Not be a resident of, or operating in, a jurisdiction where cryptocurrency payment services are prohibited or restricted by law',
                  'Not be listed on any government-issued sanctions or terrorist financing watch lists',
                  'Agree to provide accurate, current, and complete registration information',
                ]} />
              </SubSection>

              <SubSection title="4.2 Account Security">
                <Para>You are solely responsible for:</Para>
                <List items={[
                  'Maintaining the confidentiality of your login credentials (email, password)',
                  'All activities that occur under your account, whether or not authorised by you',
                  'Promptly notifying CoinPayUp of any unauthorised access or suspected security breach via our support ticket system',
                  'Ensuring your password is strong and not reused from other services',
                ]} />
                <Para>CoinPayUp will never contact you asking for your password. If you receive such a request, treat it as fraudulent.</Para>
              </SubSection>

              <SubSection title="4.3 Account Verification">
                <Para>A valid email address is required for registration. You must verify your email before gaining full platform access. CoinPayUp reserves the right to require additional identity or business verification at any time for compliance purposes.</Para>
              </SubSection>
            </Section>

            <Section id="billing" title="5. Subscription Plans & Billing">
              <SubSection title="5.1 Plan Tiers">
                <Para>CoinPayUp offers the following subscription tiers, each with defined transaction volume limits and fee structures:</Para>
                <List items={[
                  'Free Plan: Limited monthly transaction volume, 1% per-transaction fee, basic features only',
                  'Starter Plan: 200 TX/month allowance, 0% per-transaction fee, access to webhooks and basic API',
                  'Business Plan: High volume allowance, competitive fees, advanced API access, priority support',
                  'Enterprise Plan: Custom volume limits, lowest fee rates, dedicated support, SLA guarantees',
                ]} />
                <Para>Current pricing, transaction limits, and fee percentages are published on our Pricing page and are subject to change with 30 days notice.</Para>
              </SubSection>

              <SubSection title="5.2 Prepaid Account Balance">
                <Para>CoinPayUp operates on a <strong className="text-foreground">prepaid balance model</strong>. All fees are deducted automatically from your account balance — no invoices or manual payments are required for ongoing usage.</Para>
                <List items={[
                  'You must maintain a sufficient account balance to keep your plan active and enable invoice creation',
                  'Account balance can be topped up at any time via the Billing page using USDT (minimum $10)',
                  'New accounts receive a $5 signup credit, reclaimed on first real top-up',
                  'New paid-plan accounts receive a 7-day free trial (monthly fee only — per-transaction fees still apply from day one)',
                ]} />
                <InfoBox variant="warning">
                  <strong>Important:</strong> If your account balance reaches zero, new invoice creation will be blocked until you add funds.
                </InfoBox>
              </SubSection>

              <SubSection title="5.3 Automatic Fee Charges">
                <Para>The following fees are <strong className="text-foreground">charged automatically from your account balance</strong>:</Para>
                <List items={[
                  '% Per Transaction fee — deducted instantly from your balance on every verified payment received, based on your plan\'s fee rate',
                  'Extra TX Fee — a fixed per-transaction charge applied when your monthly transaction count exceeds your plan\'s included limit',
                  'Monthly Plan Fee — your plan\'s monthly subscription cost, automatically deducted from your account balance on your monthly renewal date',
                ]} />
                <Para>Per-transaction fees (% Per Transaction and Extra TX Fee) are charged on every confirmed payment, <strong className="text-foreground">including during your free trial period</strong>. The monthly plan fee is waived during the free trial only.</Para>
              </SubSection>

              <SubSection title="5.4 Monthly Plan Auto-Renewal">
                <Para>Paid plan subscriptions renew automatically each month. On your renewal date:</Para>
                <List items={[
                  'The monthly plan fee is automatically deducted from your CoinPayUp account balance',
                  'If your balance is insufficient, a 10-day grace period begins — invoice creation continues during this window',
                  'If the balance is not topped up before the grace period ends, your plan expires and invoice creation is blocked until you add funds',
                  'You can upgrade or downgrade your plan at any time from the Billing page — plan changes take effect immediately and the new plan\'s monthly fee is charged from your balance at the time of switching',
                  'Downgrading to the Free plan is always permitted at no charge',
                ]} />
                <InfoBox variant="warning">
                  <strong>Auto-Charge Notice:</strong> By activating a paid plan, you authorise CoinPayUp to automatically deduct the monthly plan fee from your account balance on each renewal date without further confirmation.
                </InfoBox>
              </SubSection>

              <SubSection title="5.5 Price Changes">
                <Para>CoinPayUp reserves the right to modify subscription prices at any time. Price changes will be communicated via email and platform notification at least 30 days before taking effect. Your continued use of the Service after a price change constitutes acceptance of the new pricing.</Para>
              </SubSection>

              <SubSection title="5.6 Taxes">
                <Para>You are responsible for any applicable taxes, levies, or duties imposed by governmental authorities in connection with your use of the Service. CoinPayUp's fees are exclusive of taxes unless stated otherwise.</Para>
              </SubSection>
            </Section>

            <Section id="refunds" title="6. Refund Policy">
              <Para>Due to the nature of cryptocurrency transactions and digital services:</Para>
              <List items={[
                'All subscription fees are non-refundable once paid, except as required by applicable consumer protection laws',
                'No refunds are issued for partial months or unused features within a billing period',
                'If CoinPayUp is unable to deliver the Service due to a verified platform fault, you may be eligible for a pro-rata credit applied to your next billing period',
                'Any refund requests must be submitted within 7 days of the disputed charge via our support ticket system',
              ]} />
              <Para>CoinPayUp does not process refunds for customer payments received by Merchants. Merchant-to-customer refunds are entirely the Merchant's responsibility.</Para>

              <SubSection title="6.1 Refund of Unused Account Balance">
                <Para>
                  Customers may request a refund of any unused balance in their CoinPayUp account at any time by submitting a support ticket. Upon verification of account ownership, the remaining balance will be refunded through an appropriate payment method.
                </Para>
                <Para>
                  If an account remains inactive and the account balance unused for a period of <strong className="text-foreground">6 months</strong>, CoinPayUp will send <strong className="text-foreground">three notification emails</strong> to the customer at their registered email address, informing them of the remaining balance and providing instructions to request a refund.
                </Para>
                <Para>
                  If no response or refund request is received after these notifications, the unused balance may be <strong className="text-foreground">donated to charity</strong>.
                </Para>
                <InfoBox variant="info">
                  <strong>Customer Protection:</strong> If a customer later contacts CoinPayUp and requests a refund of their unused balance — even after the amount has already been donated to charity — CoinPayUp will <strong>honour the request and refund the equivalent amount</strong> to the customer, subject to verification of account ownership.
                </InfoBox>
              </SubSection>
            </Section>

            <Section id="acceptable-use" title="7. Acceptable Use Policy">
              <Para>By using CoinPayUp, you agree <strong className="text-foreground">not</strong> to use the Service for:</Para>

              <SubSection title="7.1 Prohibited Activities">
                <List items={[
                  'Any illegal activity, including but not limited to money laundering, tax evasion, terrorist financing, or fraud',
                  'Processing payments for prohibited goods or services, including: controlled substances and drugs, weapons, counterfeit products, human trafficking, child exploitation material, gambling in restricted jurisdictions',
                  'Circumventing, hacking, or bypassing any security, authentication, or access control mechanisms',
                  'Reverse engineering, decompiling, disassembling, or otherwise attempting to extract source code from the platform',
                  'Scraping, crawling, or systematically extracting data from the platform without written permission',
                  'Uploading, transmitting, or distributing malicious code, viruses, or any harmful software',
                  'Impersonating any person or entity, or misrepresenting your affiliation with any person or entity',
                  'Using the Service in a way that disrupts, degrades, or impairs the functionality for other users',
                ]} />
              </SubSection>

              <SubSection title="7.2 Merchant Responsibilities">
                <Para>As a Merchant, you are solely responsible for:</Para>
                <List items={[
                  'The legality of the goods or services you sell and collect payments for',
                  'Obtaining any required licences, registrations, or permits for your business',
                  'Clearly communicating your own refund, return, and dispute policies to your customers',
                  'Compliance with applicable tax laws regarding cryptocurrency income',
                  'Ensuring your customers consent to cryptocurrency payment terms before checkout',
                  'Ensuring you have the legal right to collect and process your customers\' personal data in accordance with applicable data protection laws including GDPR',
                ]} />
              </SubSection>

              <SubSection title="7.3 AML & Compliance Monitoring">
                <Para>CoinPayUp reserves the right to monitor activity on the platform for compliance purposes, fraud prevention, anti-money laundering (AML) obligations, and sanctions enforcement. We may suspend or terminate accounts, withhold funds pending investigation, or report activity to relevant authorities where suspicious, unlawful, or non-compliant activity is detected.</Para>
                <Para>By using the Service, you agree to cooperate with any compliance-related requests, including providing identity verification or source-of-funds documentation where required by applicable law.</Para>
              </SubSection>

              <SubSection title="7.4 Sanctions & Restricted Jurisdictions">
                <Para>The Service may not be used by individuals, businesses, or entities:</Para>
                <List items={[
                  'Located in, incorporated in, or operating from jurisdictions subject to international sanctions (including but not limited to sanctions administered by the UN, EU, US OFAC, or UK OFSI)',
                  'Listed on any government-issued sanctions list, denied-party list, or terrorist financing watch list',
                  'Operating in jurisdictions where the receipt, use, or transfer of cryptocurrency is prohibited or restricted by law',
                ]} />
                <Para>By using the Service, you represent and warrant that you are not subject to any such restrictions. CoinPayUp reserves the right to terminate accounts and refuse service where sanctions obligations require.</Para>
              </SubSection>

              <SubSection title="7.5 Regulatory Compliance Disclaimer">
                <Para>CoinPayUp makes no representation that use of the Service is lawful, appropriate, or compliant with local laws in every jurisdiction. The availability of the Service does not constitute an endorsement or recommendation of its use in any particular jurisdiction.</Para>
                <Para>Merchants are solely responsible for ensuring their use of the Service, including the acceptance of cryptocurrency payments, complies with all applicable local, national, and international laws and regulations, including but not limited to financial services law, consumer protection law, tax law, and data protection law.</Para>
              </SubSection>
            </Section>

            <Section id="crypto-risks" title="8. Cryptocurrency Risks & Disclaimers">
              <Para>You acknowledge and accept the following inherent risks of cryptocurrency use:</Para>
              <List items={[
                'Irreversibility: Confirmed blockchain transactions cannot be reversed, cancelled, or recalled — by CoinPayUp or anyone else',
                'Volatility: Cryptocurrency values fluctuate significantly. CoinPayUp does not guarantee any exchange rate and is not liable for value changes between invoice creation and payment',
                'Network delays: Blockchain congestion, validator outages, or network forks may cause payment delays outside CoinPayUp\'s control',
                'Address errors: Payments sent to incorrect wallet addresses are permanently lost. Always verify wallet addresses before accepting payments',
                'Regulatory risk: Future regulatory changes may restrict or prohibit cryptocurrency usage in your jurisdiction',
                'Technology risk: Smart contract vulnerabilities, protocol upgrades, or network attacks may affect specific cryptocurrencies',
                'Tax implications: Receiving cryptocurrency as payment may constitute taxable income in your jurisdiction — consult a qualified tax advisor',
                'Blockchain network fees (gas fees, miner fees): All blockchain transaction fees are the sole responsibility of the payer or merchant as applicable. These fees are set by the blockchain network and are entirely outside CoinPayUp\'s control. CoinPayUp does not collect, reimburse, or guarantee any specific network fee amount',
              ]} />
              <InfoBox variant="warning">
                CoinPayUp does not provide financial, investment, legal, or tax advice. Any content on the platform is for informational purposes only.
              </InfoBox>
            </Section>

            <Section id="chargebacks" title="9. Chargebacks & Transaction Disputes">
              <Para>CoinPayUp is not a party to transactions between Merchants and their customers. We provide payment verification technology only and do not:</Para>
              <List items={[
                'Mediate, arbitrate, or resolve disputes between Merchants and their customers',
                'Process refunds, reversals, or chargebacks on behalf of any party',
                'Hold, escrow, or have custody over any cryptocurrency funds at any point',
                'Guarantee payment outcomes or customer satisfaction',
              ]} />
              <Para>All disputes regarding goods, services, refunds, or customer payments are exclusively between the Merchant and their customer. Merchants must maintain their own dispute resolution and refund processes and communicate them clearly to customers at the point of sale.</Para>
            </Section>

            <Section id="payment-verification" title="10. Payment Verification & Accuracy">
              <SubSection title="9.1 Verification Mechanism">
                <Para>CoinPayUp verifies payments by monitoring public blockchain data. A payment is considered verified when:</Para>
                <List items={[
                  'An on-chain transaction is detected sending cryptocurrency to the specified invoice wallet address',
                  'The received amount matches the invoiced amount within the configured tolerance settings (Exact / Round-Up / Round-Down)',
                  'The transaction achieves the required number of blockchain confirmations',
                ]} />
              </SubSection>

              <SubSection title="9.2 Tolerance Settings">
                <Para>Merchants may configure payment tolerance settings (exact match, round-up tolerance, round-down tolerance). It is the Merchant's sole responsibility to configure appropriate tolerance levels for their use case. CoinPayUp is not liable for under- or over-payments resulting from the Merchant's tolerance configuration.</Para>
              </SubSection>

              <SubSection title="9.3 Node Health & Data Accuracy">
                <Para>Payment verification relies on blockchain node availability. CoinPayUp operates redundant nodes across multiple providers. However, during periods of node downtime or blockchain network issues, payment detection may be delayed. CoinPayUp is not liable for verification delays caused by third-party blockchain infrastructure.</Para>
              </SubSection>
            </Section>

            <Section id="sla" title="11. Service Level Agreement (SLA)">
              <SubSection title="10.1 Uptime Target">
                <Para>CoinPayUp targets <strong className="text-foreground">99.5% platform uptime</strong> on Business and Enterprise plans, measured monthly excluding scheduled maintenance windows. No uptime guarantee is provided for Free and Starter plans.</Para>
              </SubSection>

              <SubSection title="10.2 Scheduled Maintenance">
                <Para>Planned maintenance that may affect Service availability will be communicated via email and in-platform notice at least 24 hours in advance. Emergency maintenance may occur without prior notice in cases of security vulnerabilities.</Para>
              </SubSection>

              <SubSection title="10.3 Remedies for Downtime">
                <Para>In the event of an SLA breach on eligible plans, your sole and exclusive remedy is a pro-rata service credit for the affected period, applied to your next billing cycle. CoinPayUp is not liable for any indirect or consequential damages resulting from service downtime.</Para>
              </SubSection>
            </Section>

            <Section id="intellectual-property" title="12. Intellectual Property">
              <Para>All intellectual property rights in and to the Service — including but not limited to software, algorithms, user interface designs, documentation, trademarks, logos, and brand assets — are owned by or licensed to CoinPayUp.</Para>
              <Para>We grant you a limited, non-exclusive, non-transferable, revocable licence to use the Service solely for its intended purpose as described in these Terms. This licence does not include the right to:</Para>
              <List items={[
                'Sublicense, sell, resell, transfer, assign, or commercially exploit the Service',
                'Modify, adapt, translate, or create derivative works based on the Service',
                'Copy, frame, or mirror any content on any other server or device',
                'Use our trademarks, logos, or brand assets without prior written consent',
              ]} />
              <Para>Any feedback, suggestions, or ideas you provide regarding the Service may be used by CoinPayUp without obligation to you.</Para>
            </Section>

            <Section id="confidentiality" title="13. Confidentiality & API Keys">
              <Para>Your API keys are credentials that grant programmatic access to your CoinPayUp account. You must:</Para>
              <List items={[
                'Keep API keys strictly confidential and never expose them in client-side code, public repositories, or unsecured communications',
                'Rotate API keys immediately if you suspect unauthorised access or compromise',
                'Not share API keys with unauthorised third parties',
                'Accept full responsibility for all API calls made using your keys',
              ]} />
              <Para>CoinPayUp will never ask for your API keys. If you receive a request claiming to be from CoinPayUp asking for your API key, treat it as fraudulent and report it immediately via our <strong className="text-foreground">support ticket system</strong>.</Para>
            </Section>

            <Section id="third-party" title="14. Third-Party Services & Integrations">
              <Para>The Service integrates with third-party providers including blockchain networks, CAPTCHA services, email delivery providers, and node infrastructure. You acknowledge that:</Para>
              <List items={[
                'Third-party services are subject to their own terms, conditions, and privacy policies',
                'CoinPayUp is not responsible for the availability, accuracy, or security of any third-party service',
                'Disruptions to third-party infrastructure (blockchain networks, node providers) may affect payment detection and are outside CoinPayUp\'s control',
                'Integration with third-party e-commerce platforms (WordPress, Shopify, etc.) is provided "as-is" and may require updates following platform changes by those providers',
              ]} />
            </Section>

            <Section id="indemnification" title="15. Indemnification">
              <Para>You agree to defend, indemnify, and hold harmless CoinPayUp, its officers, directors, employees, contractors, and agents from and against any claims, liabilities, damages, judgments, awards, losses, costs, or expenses (including reasonable legal fees) arising out of or relating to:</Para>
              <List items={[
                'Your violation of these Terms',
                'Your use of the Service, including any transactions processed through your account',
                'Your violation of any applicable law or regulation, including cryptocurrency and financial regulations',
                'Your violation of any third-party rights, including intellectual property or privacy rights',
                'Any content or data you submit, post, or transmit through the Service',
                'Any fraudulent, deceptive, or harmful activities conducted through your account',
              ]} />
            </Section>

            <Section id="liability" title="16. Limitation of Liability">
              <Para>To the maximum extent permitted by applicable law, CoinPayUp and its affiliates, officers, employees, and agents shall not be liable to you for:</Para>
              <List items={[
                'Any indirect, incidental, special, consequential, exemplary, or punitive damages',
                'Loss of profits, revenue, data, goodwill, or business opportunities',
                'Losses resulting from blockchain network failures, cryptocurrency volatility, or irreversible transactions',
                'Service interruptions or data loss resulting from events beyond our reasonable control',
                'Unauthorised access to your account data resulting from your failure to maintain credential security',
                'Damages arising from your reliance on inaccurate or incomplete on-chain data from third-party nodes',
              ]} />
              <Para>In all cases, CoinPayUp's total liability to you for any claim arising under these Terms shall not exceed the greater of: (a) the total fees paid by you to CoinPayUp in the <strong className="text-foreground">6 months immediately preceding</strong> the event giving rise to the claim, or (b) USD $100.</Para>
            </Section>

            <Section id="warranties" title="17. Disclaimer of Warranties">
              <Para>
                The Service is provided on an <strong className="text-foreground">"AS IS"</strong> and <strong className="text-foreground">"AS AVAILABLE"</strong> basis. To the fullest extent permitted by law, CoinPayUp expressly disclaims all warranties of any kind, whether express, implied, or statutory, including but not limited to:
              </Para>
              <List items={[
                'Implied warranties of merchantability, fitness for a particular purpose, and non-infringement',
                'Any warranty that the Service will be uninterrupted, error-free, secure, or virus-free',
                'Any warranty regarding the accuracy or reliability of blockchain data or payment verification',
                'Any warranty that defects will be corrected within a particular timeframe',
              ]} />
              <Para>CoinPayUp does not provide investment, financial, legal, or tax advice. Nothing on the platform constitutes a recommendation to buy, sell, or hold any cryptocurrency.</Para>
            </Section>

            <Section id="force-majeure" title="18. Force Majeure">
              <Para>CoinPayUp shall not be liable for any delay or failure to perform its obligations under these Terms where such delay or failure results from causes beyond our reasonable control, including but not limited to:</Para>
              <List items={[
                'Acts of God, natural disasters, or extreme weather events',
                'Government actions, regulations, sanctions, or embargoes',
                'Major blockchain network failures, forks, or attacks affecting supported chains',
                'Widespread internet infrastructure failures or cyber attacks',
                'Epidemics, pandemics, or public health emergencies',
                'Strikes, labour disputes, or civil unrest',
              ]} />
              <Para>In force majeure events, CoinPayUp will make reasonable efforts to notify affected Merchants and resume normal operations as quickly as possible.</Para>
            </Section>

            <Section id="termination" title="19. Termination">
              <SubSection title="18.1 Termination by You">
                <Para>You may terminate your account at any time by submitting a <strong className="text-foreground">support ticket</strong> from your dashboard. Upon termination, your right to access the Service ceases immediately. Subscription fees already paid are non-refundable. You remain responsible for any outstanding fees owed at the time of termination.</Para>
              </SubSection>

              <SubSection title="18.2 Termination by CoinPayUp">
                <Para>CoinPayUp reserves the right to suspend or permanently terminate your account, with or without notice, for:</Para>
                <List items={[
                  'Violation or breach of any provision of these Terms',
                  'Engaging in suspicious, fraudulent, or illegal activity',
                  'Non-payment of subscription fees or outstanding balances after a 14-day grace period',
                  'Receipt of a lawful request from governmental or regulatory authorities',
                  'Prolonged account inactivity (accounts inactive for more than 12 consecutive months)',
                  'Circumstances where continued service would expose CoinPayUp to legal or reputational harm',
                ]} />
              </SubSection>

              <SubSection title="18.3 Effect of Termination">
                <Para>Upon termination, your access to the Service dashboard, APIs, and data will be disabled. CoinPayUp may retain your transaction history and account data as required by applicable law. Sections 9, 11, 13, 14, 15, 16, and 19 of these Terms shall survive termination.</Para>
              </SubSection>
            </Section>

            <Section id="governing-law" title="20. Governing Law & Dispute Resolution">
              <SubSection title="19.1 Governing Law">
                <Para>These Terms shall be governed by and construed in accordance with the laws of <strong className="text-foreground">[Country/Jurisdiction]</strong>, without regard to its conflict of law principles. Any legal action or proceeding arising out of or relating to these Terms shall be subject to the exclusive jurisdiction of the courts of <strong className="text-foreground">[Country/Jurisdiction]</strong>.</Para>
              </SubSection>

              <SubSection title="19.2 Dispute Resolution">
                <Para>In the event of any dispute, claim, or controversy arising out of or relating to these Terms or the use of the Service, the parties agree to first attempt to resolve the matter through good-faith negotiation via our support ticket system. Either party may initiate this process by submitting written notice describing the dispute.</Para>
                <Para>If the dispute is not resolved within 30 days of written notice, the parties agree to submit to binding arbitration in <strong className="text-foreground">[City, Country]</strong>, unless prohibited by applicable law. Arbitration proceedings shall be conducted in English. You waive the right to participate in class action lawsuits or class-wide arbitrations against CoinPayUp.</Para>
              </SubSection>

              <SubSection title="19.3 Law Enforcement Cooperation">
                <Para>CoinPayUp may disclose information about users and transactions to law enforcement agencies, regulatory authorities, or government bodies where required or permitted by applicable law, including in response to valid legal process (such as court orders, warrants, or regulatory demands). CoinPayUp will not notify users of such disclosures where prohibited by law.</Para>
                <Para>By using the Service, you acknowledge and consent to CoinPayUp cooperating fully with any lawful requests from competent authorities, including but not limited to requests related to anti-money laundering investigations, fraud prevention, or sanctions enforcement.</Para>
              </SubSection>
            </Section>

            <Section id="changes" title="21. Changes to These Terms">
              <Para>CoinPayUp reserves the right to modify these Terms at any time. When we make material changes, we will:</Para>
              <List items={[
                'Update the "Last updated" date at the top of this page',
                'Notify registered Merchants via in-platform notice at least 14 days before changes take effect',
                'Display a prominent notice on the platform dashboard',
              ]} />
              <Para>Your continued use of the Service after the effective date of changes constitutes your acceptance of the revised Terms. If you do not agree with the updated Terms, you must stop using the Service.</Para>
            </Section>

            <Section id="contact" title="22. Contact & Legal Notices">
              <Para>For questions about these Terms, legal notices, or to report violations, please contact us:</Para>
              <div className="bg-muted/30 rounded-xl p-5 text-sm text-muted-foreground space-y-2 mt-3">
                <p className="font-semibold text-foreground text-base">CoinPayUp — Legal Team</p>
                <p>All communications are handled exclusively through our <strong className="text-foreground">support ticket system</strong>. Please log in to your merchant dashboard and open a support ticket to reach our team.</p>
                <p className="text-xs pt-2 border-t border-border">We aim to respond to all legal enquiries within <strong className="text-foreground">5 business days</strong>.</p>
              </div>
            </Section>

          </div>
        </div>
      </div>
      </main>
      <SiteFooter />
    </div>
  );
}
